import { UseFormReturnType } from "@mantine/form";
import NumberInput from "src/components/NumberInput/NumberInput";
import TextInput from "src/components/TextInput/TextInput";
import Switch from "src/components/Switch/Switch";
import Textarea from "src/components/Textarea/Textarea";
import MultiSelect from "src/components/MultiSelect";
import Select, { ISelect } from "src/components/Select";
import ActionCard from "src/components/ActionCard";
import {
  CategoryOptionType,
  useGetCategoryOptionManyLazyQuery,
  useGetLocationTaxRateNamesQuery,
} from "src/generated/graphql";
import { AppContext } from "src/api/AppContext";
import { useContext, useEffect, useState } from "react";
import { Group, Text } from "@mantine/core";
import { HeightCard } from "src/utils/constants";
import { IProductForm } from "./ProductDetail";
import ImageUpload from "src/components/ImageUpload";
import { IvariantMetrics } from "src/api/context/ProductContext";

interface ProductFormFieldsProps {
  form: UseFormReturnType<IProductForm>;
  isActive?: boolean;
  variantMetrics: IvariantMetrics;
  locationId: string | undefined;
}

const ProductFormFields = ({
  form,
  isActive = false,
  variantMetrics,
  locationId,
}: ProductFormFieldsProps) => {
  const { currentOrganization } = useContext(AppContext);
  const [tagArr, settagArr] = useState<ISelect[]>([]);
  const isTaxable = form.getInputProps("isTaxable").value;

  const [getCategories] = useGetCategoryOptionManyLazyQuery({
    onCompleted(data) {
      let _data = data?.categoryOptionMany;
      let _dataService =
        _data
          ?.filter((e) => e.type === CategoryOptionType.Tag)
          .map((val) => ({
            value: val.id,
            label: val.name,
          })) || [];
      settagArr(_dataService);
    },
  });

  const { data: taxRates } = useGetLocationTaxRateNamesQuery({
    variables: { locationId },
    skip: !locationId,
  });

  useEffect(() => {
    if (currentOrganization?.id) {
      getCategories({
        variables: { orgId: currentOrganization?.id || "" },
      });
    }
  }, [currentOrganization, getCategories]);

  return (
    <>
      <ActionCard title="General" height={HeightCard.md} mb={10}>
        <ImageUpload
          onReadSuccess={(b64) => form.setFieldValue("imgUrl", b64)}
          src={form.values.imgUrl!}
          alt="product Image"
          height="170px"
          maxWidth="100%"
          sx={{ margin: "auto" }}
          disabled={isActive}
        />
        <Group position="center">
          <Switch
            labelPosition="left"
            label="Active"
            checked={!!form.getInputProps("isActive")?.value}
            {...form.getInputProps("isActive")}
            disabled={isActive}
            mt={"sm"}
          />

          <Switch
            labelPosition="left"
            label="Taxable"
            checked={!!form.getInputProps("isTaxable")?.value}
            {...form.getInputProps("isTaxable")}
            disabled={isActive}
            mt={"sm"}
          />
        </Group>

        <TextInput
          withAsterisk
          label="Name"
          {...form.getInputProps("name")}
          disabled={isActive}
        />
        <Textarea
          label="Description"
          disabled={isActive}
          {...form.getInputProps("description")}
        />
        <Group position="apart">
          <NumberInput
            withAsterisk
            label={"Cost"}
            precision={2}
            {...form.getInputProps("cost")}
            styles={{
              root: {
                width: "47%",
              },
            }}
          />

          {isTaxable && (
            <Switch
              labelPosition="left"
              label="Value is Post-Tax"
              checked={!!form.getInputProps("isPostTax")?.value}
              {...form.getInputProps("isPostTax")}
              disabled={isActive}
              mt={"sm"}
            />
          )}

          <NumberInput
            mt={isTaxable ? 0 : "md"}
            withAsterisk
            label={"Stock"}
            {...form.getInputProps("stock")}
            styles={{
              root: {
                width: isTaxable ? "100%" : "47%",
              },
            }}
          />
        </Group>
        <TextInput
          withAsterisk
          label="SKU"
          {...form.getInputProps("sku")}
          disabled={isActive}
        />
        <TextInput
          label="Barcode"
          {...form.getInputProps("barcode")}
          disabled={isActive}
        />
        <MultiSelect
          disabled={isActive}
          label="Tags"
          data={tagArr}
          {...form.getInputProps("tags")}
        />

        {isTaxable && (
          <MultiSelect
            disabled={isActive}
            label="Tax Rates"
            data={
              taxRates?.locationTaxRateMany?.map((val) => ({
                value: val.id,
                label: val.organizationTaxRate.name,
              })) || []
            }
            {...form.getInputProps("taxRates")}
          />
        )}
      </ActionCard>

      <ActionCard
        title="Commision-able"
        height={HeightCard.md}
        mb={10}
        rightIconButton={
          <Switch
            disabled={isActive}
            checked={form.getInputProps("isCommissionable")?.value}
            {...form.getInputProps("isCommissionable")}
            hasBackground={true}
          />
        }
        hideChild={!form.getInputProps("isCommissionable")?.value}
      >
        {form.getInputProps("isCommissionable")?.value && (
          <>
            <Text size={13} ml={7} color="#000000">
              Commissions will be allowed for this product. Define the
              commission value below.
            </Text>

            <Group position="apart">
              <NumberInput
                withAsterisk
                label="Value"
                precision={2}
                {...form.getInputProps("commissionValue")}
                disabled={isActive}
                mt={"xs"}
              />
              <Switch
                labelPosition="left"
                label="Percentage"
                checked={!!form.getInputProps("isCommissionPercent")?.value}
                {...form.getInputProps("isCommissionPercent")}
                disabled={isActive}
                mt={"xs"}
              />
            </Group>
          </>
        )}
      </ActionCard>

      {!!variantMetrics?.length && (
        <ActionCard title="Variant Values" height={HeightCard.md}>
          <>
            {variantMetrics.map((e) => (
              <Select
                key={e.id}
                withAsterisk
                data={
                  e.values?.map((val) => ({
                    value: val.id,
                    label: val.name,
                  })) || []
                }
                label={e.name}
                {...form.getInputProps(`metricValues.${e?.name}`)}
              />
            ))}
          </>
        </ActionCard>
      )}
    </>
  );
};

export default ProductFormFields;
